import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getArchivedTeacherAssignments, getTeacherAssignments, TeacherAssignment } from '../api/AssignmentTeacherApi';
import ModuleContext from '../../../_contexts/ModuleContext';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';

type TeacherAssignmentsContextValue = {
  assignments: TeacherAssignment[];
  fetchAssignments: () => Promise<void>;
  isLoading: boolean;
  archived: boolean;
};

const defaultTeacherAssignmentsContextValue: TeacherAssignmentsContextValue = {
  assignments: [],
  fetchAssignments: () => Promise.resolve(),
  isLoading: false,
  archived: false,
};

const TeacherAssignmentsContext = createContext<TeacherAssignmentsContextValue>(defaultTeacherAssignmentsContextValue);

export default TeacherAssignmentsContext;

export const TeacherAssignmentsContextProvider = ({
  children,
  archived,
}: {
  children: ReactNode;
  archived: boolean;
}): JSX.Element => {
  const [assignments, setAssignments] = useState<TeacherAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleError = useThrowAsyncError();
  const { module } = useContext(ModuleContext);
  const hasDigitalManualLicense = module?.licenses.hasDigitalManualLicenseForTeacher;

  const fetchAssignments = useCallback(async () => {
    if (!module || !hasDigitalManualLicense) {
      setAssignments([]);
      return;
    }

    setIsLoading(true);

    try {
      const fetchedAssignments = archived
        ? await getArchivedTeacherAssignments(module.id)
        : await getTeacherAssignments(module.id);
      setAssignments(fetchedAssignments);
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, [module, hasDigitalManualLicense, archived, handleError]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const teacherAssignmentsContextValue = useMemo(
    () => ({ assignments, fetchAssignments, isLoading, archived }),
    [assignments, fetchAssignments, isLoading, archived],
  );

  return (
    <TeacherAssignmentsContext.Provider value={teacherAssignmentsContextValue}>
      {children}
    </TeacherAssignmentsContext.Provider>
  );
};
